import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { supabase } from '../lib/supabase';
import { useProfile } from '../hooks/auth/useProfile';

// Use the same Event type from EventsPage
interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  type: 'networking' | 'workshop' | 'webinar';
  capacity: number;
  registeredCount: number;
  image: string;
}


export function EventRegistrationPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [formData, setFormData] = useState({
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: user?.email || '',
    company: profile?.company || '',
    title: profile?.position || '',
    dietaryRestrictions: '',
    specialRequirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
  try {
    // First, get the basic event data
    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select(`
        id,
        title,
        description,
        date,
        time,
        location,
        type,
        capacity,
        image_url
      `)
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;
    
    // Then, get the registration count separately
    const { count: registeredCount, error: countError } = await supabase
      .from('event_registrations')
      .select('*', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'confirmed');
      
    if (countError) throw countError;
    
    // Combine the data
    setEvent({
      ...eventData,
      registeredCount: registeredCount || 0,
      image: eventData.image_url // Map image_url to image for consistency
    });
  } catch (error) {
    console.error('Error loading event:', error);
    toast.error('Failed to load event details');
    navigate('/events');
  } finally {
    setIsLoading(false);
  }
};

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#ddd9d9] py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex justify-center">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#ddd9d9] py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-xl p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-black mb-4">Event Not Found</h2>
            <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/events"
              className="inline-flex items-center text-[#800020] hover:opacity-80"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!user) return;

    try {
      const { error } = await supabase
        .from('event_registrations')
        .insert({
          event_id: eventId,
          user_id: user.id,
          status: 'confirmed',
          dietary_restrictions: formData.dietaryRestrictions,
          special_requirements: formData.specialRequirements
        });

      if (error) throw error;
      
      toast.success('Registration successful!');
      navigate('/prestige-club');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Failed to register for event');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/events"
            className="inline-flex items-center text-[#800020] hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Events
          </Link>
        </div>

        <div className="bg-white rounded-xl overflow-hidden shadow-lg">
          {/* Event Header */}
          <div className="relative h-48">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <h1 className="text-3xl font-bold text-white text-center px-4">
                Register for {event.title}
              </h1>
            </div>
          </div>

          {/* Event Details */}
          <div className="p-6 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-2" />
                <span>
                  {new Date(event.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  <br />
                  {event.time}
                </span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="h-5 w-5 mr-2" />
                <span>{event.location}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Users className="h-5 w-5 mr-2" />
                <span>{event.capacity - event.registeredCount} spots remaining</span>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  <div>
    <label className="block text-sm font-medium text-black mb-2">
      First Name*
    </label>
    <input
      type="text"
      value={formData.firstName}
      onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
      required
    />
  </div>

  <div>
    <label className="block text-sm font-medium text-black mb-2">
      Last Name*
    </label>
    <input
      type="text"
      value={formData.lastName}
      onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
      required
    />
  </div>
</div>

<div>
  <label className="block text-sm font-medium text-black mb-2">
    Email*
  </label>
  <input
    type="email"
    value={formData.email}
    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
    required
  />
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  <div>
    <label className="block text-sm font-medium text-black mb-2">
      Company
    </label>
    <input
      type="text"
      value={formData.company}
      onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
    />
  </div>

  <div>
    <label className="block text-sm font-medium text-black mb-2">
      Job Title
    </label>
    <input
      type="text"
      value={formData.title}
      onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
    />
  </div>
</div>

<div>
  <label className="block text-sm font-medium text-black mb-2">
    Dietary Restrictions
  </label>
  <input
    type="text"
    value={formData.dietaryRestrictions}
    onChange={e => setFormData(prev => ({ ...prev, dietaryRestrictions: e.target.value }))}
    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
    placeholder="Optional"
  />
</div>

<div>
  <label className="block text-sm font-medium text-black mb-2">
    Special Requirements
  </label>
  <textarea
    value={formData.specialRequirements}
    onChange={e => setFormData(prev => ({ ...prev, specialRequirements: e.target.value }))}
    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020]"
    rows={3}
    placeholder="Any accessibility requirements or other special accommodations needed"
  />
</div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#800020] text-white rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Registering...</span>
                  </>
                ) : (
                  'Complete Registration'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}